import * as React from "react";

import Hint from "@client/controls/Hint/component";
import { parseQueryString } from "@client/lib/api";
import date from "@client/lib/date";
import { type I18n } from "@client/types";
import { type BrandKey } from "@common/declarations";
import { i18nFormat } from "@common/lib/i18n";
import { toCamelCase } from "@common/lib/misc";
import { type Purchase } from "./types";

export const getBillingFrequency = (pckg: Purchase.Package): Purchase.BillingFrequencyKey =>
  parseQueryString().f ?? pckg.defaultBillingFrequencyKey;

export const getCodes = (): Purchase.Api.Codes => {
  const qs = parseQueryString();

  return {
    packageCode: qs.p || null,
    trialCode: qs.t || null
  };
};

const getFeaturesInfo = (i18n: I18n, brand: BrandKey) => {
  switch (brand) {
    case "Expomarket":
      return i18n.infoExpomarketFeatures;
    case "Funnelr":
      return i18n.infoFunnelrFeatures;
    case "Netready":
      return i18n.infoNetreadyFeatures;
  }
};

const formatPrice = (i18n: I18n, price: number, countryCode: string | null): string => {
  const tax = countryCode === "GB" ? price * 0.2 : 0;

  const res = `$${price.toFixed(2)}`;
  return tax ? `${res} + $${tax.toFixed(2)} ${toCamelCase(i18n.tax)}` : res;
};

export const getTitle = (
  i18n: I18n,
  pckg: Purchase.Package,
  trial: Purchase.Trial | null,
  countryCode: string | null
) => {
  if (trial?.usdPrice) {
    return i18nFormat(
      i18n.infoPaidTrialTitle,
      ["PERIOD", date.formatDuration(trial.period, trial.periodTimeUnitKey, "show")],
      ["PRICE", formatPrice(i18n, trial.usdPrice, countryCode)],
      ["BRAND", pckg.brandKey]
    );
  }

  if (trial && !trial.usdPrice) {
    return i18nFormat(
      i18n.infoFreeTrialTitle,
      ["PERIOD", date.formatDuration(trial.period, trial.periodTimeUnitKey, "show")],
      ["BRAND", pckg.brandKey]
    );
  }

  return i18nFormat(i18n.infoSignupTitle, ["BRAND", pckg.brandKey]);
};

export const getTopInfo = (i18n: I18n, pckg: Purchase.Package) => (
  <Hint hint={i18nFormat(i18n.infoSubtitle, ["BRAND", pckg.brandKey])} />
);

const getSubscriptonPriceInfo = (
  i18n: I18n,
  pckg: Purchase.Package,
  trial: Purchase.Api.Trial | null,
  countryCode: string | null
) => {
  const packagePrice = getInitialPackagePrice(pckg);
  const subscriptionPrice = `$${formatPrice(i18n, packagePrice, countryCode)}/${toCamelCase(
    getBillingFrequency(pckg)
  )}`;

  if (trial) {
    return i18nFormat(
      i18n.infoTrialPrice,
      ["TRIAL_PERIOD", trial.period],
      ["TIME_UNIT", toCamelCase(trial.periodTimeUnitKey)],
      ["PRICE", subscriptionPrice],
      ["BRAND", pckg.brandKey]
    );
  }

  return i18nFormat(i18n.infoPrice, ["PRICE", subscriptionPrice], ["BRAND", pckg.brandKey]);
};
export const getBottomInfo = (
  i18n: I18n,
  pckg: Purchase.Package,
  trial: Purchase.Api.Trial | null,
  countryCode: string | null
) => {
  return (
    <Hint
      hint={i18nFormat(
        i18n.infoDescription,
        ["BRAND", pckg.brandKey],
        ["FEATURES", getFeaturesInfo(i18n, pckg.brandKey!)],
        ["PRICE_INFO", getSubscriptonPriceInfo(i18n, pckg, trial, countryCode)]
      )}
    ></Hint>
  );
};

export const getInitialPackagePrice = (pckg: Purchase.Package): number => {
  switch (getBillingFrequency(pckg)) {
    case "Month":
      return pckg.usdMonthPrice;
    case "Quarter":
      return pckg.usdQuarterPrice;
    case "Year":
      return pckg.usdYearPrice;
  }
};

export const getInitialPrice = (pckg: Purchase.Package, trial: Purchase.Api.Trial | null): number => {
  if (trial) return trial.usdPrice;
  return getInitialPackagePrice(pckg);
};
